import { BrowserRouter, Route, Routes } from 'react-router';

import { BackToGamesBar, MobileBackButton } from '@/back-bar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ScrollToTop } from '@/components/ScrollToTop';
import {
  QuietHoursHub,
  QuietHoursSession,
  QuietHoursSessionConfig,
} from '@/experiences/quiet-hours';
import { CrosswordHub } from '@/games/crossword/CrosswordHub';
import { AtlasHub } from '@/games/atlas/AtlasHub';
import { BingoHub } from '@/games/bingo/BingoHub';
import { FiveHub } from '@/games/five/FiveHub';
import { SolitaireHub } from '@/games/solitaire/SolitaireHub';
import { MahjongHub } from '@/games/mahjong/MahjongHub';
import { GamesHub } from '@/games-hub/GamesHub';
import { WheelPrototype } from '@/games/the-wheel/WheelPrototype';
import { WheelHub } from '@/variants/the-wheel/WheelHub';
import { HostProvider } from '@/host/HostContext';
import { GameHostPage } from '@/pages/GameHostPage';
import {
  PreferencesBar,
  PreferencesPopout,
  PreferencesProvider,
} from '@/preferences';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { getActiveVariant } from '@/variants';

export function App() {
  // Resolve the active variant once per render. Today this is a constant
  // (`reading-room`); once additional variants land the selector will read
  // from Preferences and/or a host-config override. The variant's `Hub` is
  // the trivia categories hub at `/trivia`; `SudokuHub` is the sudoku
  // difficulty picker at `/sudoku`; `WordSearchHub` is the themed-shape word-
  // search picker at `/word-search`. All are optional — variants ship a
  // subset. The top-level Games Hub at `/` is variant-independent.
  const { Hub: TriviaHub, SudokuHub, WordSearchHub, CategoriesHub } = getActiveVariant();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <PreferencesProvider>
          <HostProvider>
            <ThemeProvider>
              <div className="app-shell">
                {/* Desktop chrome — labeled pill cluster + back pill. Both
                 * return null at <=768px; the mobile chrome below takes
                 * over there. */}
                <PreferencesBar />
                <BackToGamesBar />
                {/* Mobile chrome — single 44px circular trigger + popout
                 * panel for preferences, single 44px circular arrow for
                 * back. Both return null above 768px. */}
                <PreferencesPopout />
                <MobileBackButton />
                <main className="app-main" id="main">
                  <Routes>
                    <Route path="/" element={<GamesHub />} />
                    <Route path="/coming-soon" element={<GamesHub shelf="coming-soon" />} />
                    <Route
                      path="/trivia"
                      element={TriviaHub ? <TriviaHub /> : <GamesHub />}
                    />
                    <Route path="/five" element={<FiveHub />} />
                    <Route path="/wheel" element={<WheelHub />} />
                    <Route path="/wheel-lab" element={<WheelPrototype />} />
                    <Route path="/solitaire" element={<SolitaireHub />} />
                    <Route path="/crossword" element={<CrosswordHub />} />
                    <Route path="/mahjong" element={<MahjongHub />} />
                    <Route path="/atlas" element={<AtlasHub />} />
                    <Route path="/bingo" element={<BingoHub />} />
                    <Route
                      path="/sudoku"
                      element={SudokuHub ? <SudokuHub /> : <GamesHub />}
                    />
                    <Route
                      path="/word-search"
                      element={WordSearchHub ? <WordSearchHub /> : <GamesHub />}
                    />
                    <Route
                      path="/categories"
                      element={CategoriesHub ? <CategoriesHub /> : <GamesHub />}
                    />
                    <Route path="/quiet-hours" element={<QuietHoursHub />} />
                    <Route
                      path="/quiet-hours/:category"
                      element={<QuietHoursSessionConfig />}
                    />
                    <Route
                      path="/quiet-hours/:category/:duration"
                      element={<QuietHoursSession />}
                    />
                    <Route path="/play/:slug" element={<GameHostPage />} />
                    <Route path="*" element={<GamesHub />} />
                  </Routes>
                </main>
              </div>
            </ThemeProvider>
          </HostProvider>
        </PreferencesProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
